import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  addMoney, Banner, Button, ButtonLink, Card, Dialog, formatMoney, Money, negate, PageHeader, Pill, Tabs, type LinkComponent,
} from '@elysian/ui';
import type { DocumentListQuery, DocumentSort } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { ExportMenu } from '../shared/ExportMenu';
import { salesColumns } from '../shared/exportColumns';
import { iso, invoiceState, SEND_STATUS, shortDate, type InvoiceFilter } from './shared';
import { Pager, ShowSize, SortTh, useRemembered, useSettled, type SortState } from '../shared/ListControls';

const RouterLink = Link as unknown as LinkComponent;

type DateRange = { on: boolean; from: string; to: string };
const RANGE_KEY = 'elysian.invoices.dates';
// Remembered for the session, so the range is still set after opening an invoice and coming back.
function savedRange(now: string): DateRange {
  try {
    const r = JSON.parse(sessionStorage.getItem(RANGE_KEY) ?? 'null') as DateRange | null;
    if (r && typeof r.on === 'boolean') return r;
  } catch { /* not available: start afresh */ }
  return { on: false, from: `${now.slice(0, 7)}-01`, to: now };
}

export function InvoicesList() {
  const { source, today, can, context } = useSession();
  const base = context?.organisation.base_currency ?? 'EUR';
  const toast = useToast((t) => t.show);
  const navigate = useNavigate();
  const now = iso(today());
  const [params] = useSearchParams();
  // Where you were (tab, search, page) is kept for the session, so coming back from an invoice finds it again (#49).
  const [filter, setFilter] = useRemembered<InvoiceFilter>('elysian.invoices.tab', 'all', 'session');
  const [q, setQ] = useRemembered('elysian.invoices.q', '', 'session');
  const [page, setPage] = useRemembered('elysian.invoices.page', 0, 'session');
  const [size, setSize] = useRemembered('elysian.invoices.size', 50);
  const [sortBy, setSortBy] = useRemembered<SortState<DocumentSort>>('elysian.invoices.sort', { sort: 'issued', dir: 'desc' });
  const [range, setRange] = useState<DateRange>(() => savedRange(now));
  const [version, setVersion] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [posting, setPosting] = useState<{ done: number; of: number } | null>(null);
  const [failures, setFailures] = useState<{ id: string; name: string; error: string }[]>([]);
  const mayPost = can('documents.write');
  useEffect(() => { if (params.get('show') === 'credit') setFilter('credit'); }, [params, setFilter]);

  useEffect(() => {
    try { sessionStorage.setItem(RANGE_KEY, JSON.stringify(range)); } catch { /* fine: just not remembered */ }
  }, [range]);

  // The date range (#8) narrows everything below it, the tab counts included. All filtering, sorting and
  // counting happens on the server, over every invoice and credit note (#49, #50).
  const rangeError = range.on && range.from && range.to && range.from > range.to ? 'The start is after the end' : null;
  const settledQ = useSettled(q);
  const query: DocumentListQuery = {
    side: 'sales', tab: filter, ...(settledQ.trim() ? { q: settledQ.trim() } : {}),
    ...(range.on && !rangeError ? { ...(range.from ? { from: range.from } : {}), ...(range.to ? { to: range.to } : {}) } : {}),
    sort: sortBy.sort, dir: sortBy.dir,
  };
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setPage(0);
  }, [filter, settledQ, range.on, range.from, range.to, sortBy.sort, sortBy.dir, size, setPage]);
  const data = useAsync(() => source.documentList({ ...query, ...(size ? { limit: size, offset: page * size } : {}) }),
    [source, version, JSON.stringify(query), size, page], { keep: true });
  const list = data.state === 'ready' ? data.data : null;
  const rows = list?.items ?? [];
  const counts = list?.counts;
  const count = (f: InvoiceFilter) => counts?.[f] ?? 0;
  const sort = (s: SortState<DocumentSort>) => setSortBy(s);

  // Ticking drafts to post together (#4). Only drafts in view can be ticked.
  const draftsInView = rows.filter((d) => d.status === 'draft');
  const chosen = draftsInView.filter((d) => picked.has(d.id));
  const ticking = mayPost && draftsInView.length > 0;
  const toggle = (id: string, on: boolean) => setPicked((p) => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });
  const allTicked = chosen.length > 0 && chosen.length === draftsInView.length;
  // A total only when they're all in one currency.
  const chosenTotal = chosen.length && chosen.every((d) => d.currency === chosen[0]!.currency)
    ? formatMoney(addMoney('0', ...chosen.map((d) => d.gross_total)), chosen[0]!.currency) : null;

  const allCredits = chosen.length > 0 && chosen.every((d) => d.type === 'sales_credit');
  const allInvoices = chosen.every((d) => d.type !== 'sales_credit');
  const noun = (n: number) => (allInvoices ? (n === 1 ? 'invoice' : 'invoices') : allCredits ? (n === 1 ? 'credit note' : 'credit notes') : (n === 1 ? 'document' : 'invoices and credit notes'));

  async function postChosen() {
    const list = [...chosen].sort((a, b) => a.issue_date.localeCompare(b.issue_date) || a.contact_name.localeCompare(b.contact_name));
    setConfirming(false);
    setFailures([]);
    setPosting({ done: 0, of: list.length });
    const failed: { id: string; name: string; error: string }[] = [];
    const numbers: string[] = [];
    for (const [i, d] of list.entries()) {
      try {
        const posted = await source.postDocument(d.id);
        if (posted.doc_no) numbers.push(posted.doc_no);
      } catch (e) {
        failed.push({ id: d.id, name: `${d.contact_name}, ${shortDate(d.issue_date)}`, error: e instanceof Error ? e.message : String(e) });
      }
      setPosting({ done: i + 1, of: list.length });
    }
    setPosting(null);
    setPicked(new Set(failed.map((f) => f.id)));
    setFailures(failed);
    setVersion((v) => v + 1);
    if (numbers.length) {
      toast(numbers.length === 1 ? `${numbers[0]} posted to the ledger`
        : `${numbers.length} ${noun(numbers.length)} posted to the ledger: ${numbers[0]} to ${numbers[numbers.length - 1]}`);
    }
  }

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle={list
          ? `${formatMoney(list.totals.unpaid, base)} unpaid across ${list.counts.unpaid} ${list.counts.unpaid === 1 ? 'invoice' : 'invoices'}, ${formatMoney(list.totals.overdue, base)} of it overdue${range.on && !rangeError ? ', in these dates' : ''}`
          : 'Sales invoices'}
        actions={<>
          {mayPost && <ButtonLink as={RouterLink} to="/invoices/import" variant="ghost">Import</ButtonLink>}
          <ButtonLink as={RouterLink} to="/credit-notes/new">New credit note</ButtonLink>
          <ButtonLink as={RouterLink} to="/invoices/new" variant="primary">New invoice</ButtonLink>
        </>}
      />
      {data.state === 'error' && <Banner>Couldn’t load invoices: {data.error.message}</Banner>}
      <Card>
        <div className="el-spread" style={{ padding: '14px 16px 0' }}>
          <label className="el-search" style={{ minWidth: 'min(280px, 100%)', flex: '0 1 320px' }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" /><path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
            <input id="invoice-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Number or customer" aria-label="Search invoices" />
          </label>
          <div className="el-date-filter">
            <ShowSize value={size} onChange={setSize} />
            <ExportMenu name={filter === 'credit' ? 'Credit notes' : 'Invoices'} columns={salesColumns} rows={rows} disabled={!list}
              all={list ? { count: list.count, load: async () => (await source.documentList(query)).items } : undefined} />
            <label className="el-check"><input type="checkbox" checked={range.on} onChange={(e) => setRange({ ...range, on: e.target.checked })} /> Filter by date</label>
            {range.on && (
              <>
                <label className="el-date-filter-field">
                  <span>From</span>
                  <input type="date" className="el-input" aria-label="Issued from" value={range.from} max={range.to || undefined}
                    onChange={(e) => setRange({ ...range, from: e.target.value })} />
                </label>
                <label className="el-date-filter-field">
                  <span>To</span>
                  <input type="date" className="el-input" aria-label="Issued to" value={range.to} min={range.from || undefined}
                    onChange={(e) => setRange({ ...range, to: e.target.value })} />
                </label>
                {rangeError && <span className="el-error-text" role="alert">{rangeError}</span>}
              </>
            )}
          </div>
        </div>
        <Tabs<InvoiceFilter>
          label="Show invoices"
          value={filter}
          onChange={setFilter}
          tabs={[
            { key: 'all', label: 'All', count: count('all') },
            { key: 'draft', label: 'Drafts', count: count('draft') },
            { key: 'unpaid', label: 'Unpaid', count: count('unpaid') },
            { key: 'overdue', label: 'Overdue', count: count('overdue') },
            { key: 'paid', label: 'Paid', count: count('paid') },
            { key: 'credit', label: 'Credit notes', count: count('credit') },
            { key: 'void', label: 'Void', count: count('void') },
          ]}
        />
        {(chosen.length > 0 || posting) && (
          <div className="el-bulk-bar" role="region" aria-label="Selected invoices">
            <span>{posting ? `Posting ${posting.done + 1 > posting.of ? posting.of : posting.done + 1} of ${posting.of}…`
              : `${chosen.length} draft${chosen.length === 1 ? '' : 's'} selected${chosenTotal ? ` · ${chosenTotal}` : ''}`}</span>
            {!posting && <>
              <Button variant="primary" size="sm" onClick={() => setConfirming(true)}>Approve and post {chosen.length}</Button>
              <Button variant="ghost" size="sm" onClick={() => setPicked(new Set())}>Clear</Button>
            </>}
          </div>
        )}
        {failures.length > 0 && (
          <div style={{ padding: '0 16px' }}>
            <Banner>
              {failures.length === 1 ? 'One invoice wasn’t posted' : `${failures.length} invoices weren’t posted`}; {failures.length === 1 ? 'it’s' : 'they’re'} still ticked:
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{failures.map((f) => <li key={f.name}>{f.name}: {f.error}</li>)}</ul>
            </Banner>
          </div>
        )}
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Invoices">
            <thead>
              <tr>
                {ticking && (
                  <th scope="col" className="el-tick-col">
                    <input type="checkbox" aria-label="Select all drafts shown" checked={allTicked} disabled={Boolean(posting)}
                      ref={(el) => { if (el) el.indeterminate = chosen.length > 0 && !allTicked; }}
                      onChange={(e) => setPicked(e.target.checked ? new Set(draftsInView.map((d) => d.id)) : new Set())} />
                  </th>
                )}
                <SortTh label="Number" col="number" state={sortBy} onSort={sort} />
                <SortTh label="Customer" col="contact" state={sortBy} onSort={sort} />
                <SortTh label="Issued" col="issued" state={sortBy} onSort={sort} firstDir="desc" />
                <SortTh label="Due" col="due" state={sortBy} onSort={sort} firstDir="desc" />
                <th scope="col">Status</th>
                <SortTh label="Total" col="total" state={sortBy} onSort={sort} numeric />
                <SortTh label="Outstanding" col="outstanding" state={sortBy} onSort={sort} numeric />
              </tr>
            </thead>
            <tbody>
              {data.state === 'loading' && (
                <tr><td colSpan={ticking ? 8 : 7}><div className="el-skeleton" style={{ height: 18 }} aria-label="Loading invoices" /></td></tr>
              )}
              {data.state === 'ready' && rows.length === 0 && (
                <tr><td colSpan={ticking ? 8 : 7} className="el-muted">{settledQ.trim() ? 'No invoices match.' : range.on ? 'No invoices here in these dates.' : count('all') ? 'None here.' : 'No invoices or credit notes yet. Start with “New invoice”.'}</td></tr>
              )}
              {rows.map((d) => {
                const s = invoiceState(d, now);
                const sent = d.send_status ? SEND_STATUS[d.send_status] : undefined;
                const credit = d.type === 'sales_credit';
                const href = credit ? `/credit-notes/${d.id}` : `/invoices/${d.id}`;
                return (
                  <tr key={d.id} data-href={href} aria-selected={picked.has(d.id) || undefined}
                    onClick={(e) => (e.target as HTMLElement).closest('a, input, .el-tick-col') || navigate(href)}>
                    {ticking && (
                      <td className="el-tick-col">
                        {d.status === 'draft' && (
                          <input type="checkbox" aria-label={`Select draft for ${d.contact_name}, ${shortDate(d.issue_date)}`} checked={picked.has(d.id)}
                            disabled={Boolean(posting)} onChange={(e) => toggle(d.id, e.target.checked)} />
                        )}
                      </td>
                    )}
                    <td>
                      <Link className="el-row-link" to={href}>{d.doc_no ?? d.import_doc_no ?? 'Draft'}</Link>
                      {credit && <div className="el-hint">Credit note</div>}
                      {!d.doc_no && d.import_doc_no && <div className="el-hint">Imported draft</div>}
                    </td>
                    <td>{d.contact_name}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{shortDate(d.issue_date)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{d.due_date && !credit ? shortDate(d.due_date) : '—'}</td>
                    <td>
                      <Pill tone={s.tone}>{s.label}</Pill>
                      {sent && <div className="el-hint" style={sent.tone ? { color: 'var(--el-danger-ink)' } : undefined}>{sent.label}</div>}
                    </td>
                    <td className="el-num"><Money value={credit ? negate(d.gross_total) : d.gross_total} currency={d.currency} /></td>
                    <td className="el-num">{d.status === 'draft' || d.status === 'void' ? '—' : <Money value={credit ? negate(d.outstanding) : d.outstanding} currency={d.currency} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {list && <Pager count={list.count} page={page} size={size} shown={rows.length} onPage={setPage} />}
      </Card>
      {confirming && (
        <Dialog title={`Post ${chosen.length} ${noun(chosen.length)}?`} onClose={() => setConfirming(false)}
          actions={<>
            <Button variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => void postChosen()}>Approve and post {chosen.length}</Button>
          </>}>
          <p style={{ margin: 0 }}>
            {chosen.length === 1 ? 'It' : 'They'}’ll be numbered in date order (imported drafts keep their own numbers) and go onto the customers’ accounts{chosenTotal ? `, ${chosenTotal} in all` : ''}.
            Posted invoices can’t be edited, only credited. Nothing is emailed yet: send each one from the invoice.
          </p>
          <ul className="el-hint" style={{ margin: '10px 0 0', paddingLeft: 18, maxHeight: 180, overflow: 'auto' }}>
            {chosen.map((d) => <li key={d.id}>{d.contact_name}, {shortDate(d.issue_date)} · {formatMoney(d.gross_total, d.currency)}</li>)}
          </ul>
        </Dialog>
      )}
    </>
  );
}
